import React, { Component } from 'react';
import AlertContainer from 'react-alert';
import 'video.js/dist/video-js.css';
import videojs from 'video.js';
import 'webrtc-adapter';
import 'videojs-record/dist/css/videojs.record.css';
import 'videojs-record/dist/videojs.record.js';
import ReactGA from 'react-ga';
import { Dimmer, Loader } from 'semantic-ui-react';

import InstructionModal from '../InstructionModal';
import ButtonWithIcon from '../ButtonWithIcon';
import {
  uploadFileToS3,
  createPresignedUrl,
  createPresignedUrlVideoMessage
} from '../InputImage/S3Uploader';

const H264Codec = 'video/webm;codecs=h264';
const VP8Codec = 'video/webm;codecs=vp8';

export default class Recorder extends Component {
  constructor(props) {
    super(props);
    this.state = {
      videoUrl: null,
      recording: false,
      videoBlob: '',
      uploadingVideo: false,
      showAndHideButton: true,
      showWebCam: true,
      time: {},
      seconds: 3,
      startTimer: false,
      startRecording: true,
      isOpen: false
    };
    this.alertOptions = {
      offset: 14,
      position: 'top left',
      theme: 'light',
      time: 500000,
      transition: 'scale'
    };
    this.timer = 0;
    this.startTimer = this.startTimer.bind(this);
    this.countDown = this.countDown.bind(this);
  }

  componentDidMount() {
    // instantiate Video.js
    this.player = videojs(this.videoNode, this.props.videoJsOptions);

    this.player.record().getDevice();

    // user completed recording and stream is available
    this.player.on('finishRecord', () => {
      const videoUrl = window.URL.createObjectURL(this.player.recordedData);
      this.setState({ videoUrl });

      const videoFile = new File([this.player.recordedData], 'recorded-video.mp4', {
        type: 'video/mp4'
      });

      this.setState({ videoBlob: videoFile });
    });

    // error handling
    this.player.on('error', (element, error) => {
      // console.log('=> player.onError', error);
    });

    this.player.on('deviceError', () => {
      // console.log('=> player.onDeviceError', this.player.deviceErrorCode);
    });
  }

  componentWillUnmount() {
    this.player.record().stopDevice();
  }

  startTimer() {
    if (this.timer == 0 && this.state.seconds > 0) {
      this.timer = setInterval(this.countDown, 1000);
    }
  }

  countDown() {
    const { startRecording } = this.state;
    let recorder = this.player.record();
    // Remove one second, set state so a re-render happens.

    let seconds = this.state.seconds - 1;
    this.setState({
      time: this.secondsToTime(seconds),
      seconds: seconds
    });

    // Check if we're at zero.
    if (seconds === 0) {
      this.setState({ startTimer: false });
      clearInterval(this.timer);
      this.setState({
        time: {}
      });
      recorder.start();
      this.setState({ startRecording: !startRecording });
    }
  }

  secondsToTime(secs) {
    const divisor_for_minutes = secs % (60 * 60);
    const divisor_for_seconds = divisor_for_minutes % 60;
    const seconds = Math.ceil(divisor_for_seconds);

    return { seconds };
  }

  retryUploadError = async () => {
    const text =
      'Failed to upload the recorded video.\nPlease check your internet connection and click the button below to try again.';
    return (
      <section>
        <h3 className="upload-recording-error">{text}</h3>
        <ButtonWithIcon
          label="Retry Upload"
          icon="cancel"
          onClick={async () => {
            await this.handleUploadFile();
          }}
        />
      </section>
    );
  };

  handleUploadFile = async () => {
    this.msg.removeAll();

    const { videoBlob: file } = this.state;

    const {
      onUploadRecordVideo,
      onUploadVideoSuccess,
      onUploadVideoError,
      videoId,
      uniqueWebKey,
      videoUrl,
      onRecordedVideoUploadStart,
      onRecordedVideoUploadEnd
    } = this.props;

    this.setState({ uploadingVideo: true });
    onRecordedVideoUploadStart && onRecordedVideoUploadStart();

    try {
      const presignedUrlData = this.props.campaignVideo
        ? await createPresignedUrlVideoMessage(file, videoId)
        : await createPresignedUrl();
      await uploadFileToS3({ presignedUrl: presignedUrlData, file });
      await onUploadRecordVideo(presignedUrlData, videoId, uniqueWebKey, videoUrl);
      onUploadVideoSuccess && onUploadVideoSuccess();
    } catch (error) {
      onUploadVideoError && onUploadVideoError(false);
      this.msg.error(await this.retryUploadError());
    } finally {
      this.setState({ uploadingVideo: false });
      onRecordedVideoUploadEnd && onRecordedVideoUploadEnd();
    }
  };

  // Check if H264 Codec is supported by current browser. If not (Firefox, old IE/Safari), use VP8 instead
  getMediaRecorderOptions = () => {
    try {
      return { mimeType: MediaRecorder.isTypeSupported(H264Codec) ? H264Codec : VP8Codec };
    } catch (error) {
      return {};
    }
  };

  startRecording = async () => {
    const { startRecording, videoUrl } = this.state;
    const analyticsAction = !startRecording && !videoUrl ? 'Stop recording' : 'Start recording';

    ReactGA.event({
      category: 'Video',
      action: analyticsAction
    });

    const recorder = this.player.record();
    if (!recorder.isRecording()) {
      if (navigator.mediaDevices) {
        const constraints = { audio: true, video: true };
        const options = this.getMediaRecorderOptions();

        try {
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          new MediaRecorder(stream, options);
          this.setState({ startTimer: true });
          this.startTimer();
        } catch (err) {
          this.setState({ isOpen: true });
        }
      }
    } else {
      recorder.stop();
    }
  };

  redo = () => {
    ReactGA.event({
      category: 'Video',
      action: 'Redo recording video'
    });

    this.player.record().getDevice();
    this.setState({
      videoUrl: null,
      startRecording: true,
      videoBlob: null,
      seconds: 3
    });
    this.timer = 0;
  };

  onClose = () => {
    const { isOpen } = this.state;
    this.setState({ isOpen: !isOpen });
  };

  handleCancelRecord = () => {
    const { onCancelRecord } = this.props;
    onCancelRecord && onCancelRecord();
  };

  render() {
    const { videoUrl, uploadingVideo, startTimer, startRecording, isOpen, time } = this.state;
    const { styleButton } = this.props;

    const content = [
      'For macOS:',
      'Go to Safari → Preferences → Advanced',
      'Enable the option to “Show Develop menu in menu bar” at the bottom',
      'Go to Develop → Experimental Features',
      'Enable MediaRecorder.',
      'For iOS:',
      'Go to Settings → Safari → Advanced → Experimental Features',
      'Enable MediaRecorder.'
    ];
    const header =
      'Something went wrong starting Media Recorder. Please follow these instructions to it enable it on your browser.';
    const className = 'desktopRecording';
    const note = 'Note: Please refresh the page after you enable MediaRecorder.';
    const buttonText = 'Ok';

    return (
      <React.Fragment>
        {startTimer && (
          <Dimmer active inverted>
            <Loader inverted>{time.seconds}</Loader>
          </Dimmer>
        )}
        <div data-vjs-player>
          <video
            id="myVideo"
            playsInline
            ref={(node) => (this.videoNode = node)}
            className="video-js vjs-default-skin"
          />
        </div>
        <div className="textCenter mt10">
          {uploadingVideo && (
            <Dimmer active inverted>
              <Loader inverted>Uploading</Loader>
            </Dimmer>
          )}
          <div className="video-recorder-buttons">
            {startRecording && (
              <React.Fragment>
                <ButtonWithIcon
                  label="Cancel Recording"
                  icon="cancel"
                  style={styleButton}
                  onClick={this.handleCancelRecord}
                />
                <ButtonWithIcon
                  label="Start Recording"
                  icon="circle"
                  style={styleButton}
                  onClick={this.startRecording}
                />
              </React.Fragment>
            )}
            {!startRecording && !videoUrl && (
              <ButtonWithIcon
                label="Stop Recording"
                icon="stop"
                style={styleButton}
                onClick={this.startRecording}
              />
            )}
            {videoUrl && (
              <ButtonWithIcon
                label="Redo"
                icon="redo"
                style={styleButton}
                onClick={this.redo}
                disabled={uploadingVideo}
              />
            )}
            {videoUrl && (
              <ButtonWithIcon
                label="Upload"
                icon="upload"
                style={styleButton}
                onClick={this.handleUploadFile}
                loading={uploadingVideo}
                disabled={uploadingVideo}
              />
            )}
          </div>
        </div>
        <InstructionModal
          content={content}
          isOpen={isOpen}
          header={header}
          className={className}
          close={this.onClose}
          note={note}
          buttonText={buttonText}
        />
        <div className="ui splashbox thanksAlert">
          <AlertContainer ref={(a) => (this.msg = a)} {...this.alertOptions} />
        </div>
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/VideoRecorder/Recorder.js